import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import { AnalysisModel } from '../lib/analysis-models';

interface AnalysisModelsResponse {
  success: boolean;
  data: AnalysisModel[];
}

export function useActiveAnalysisModels() {
  return useQuery({
    queryKey: ['analysis-models', 'active'],
    queryFn: async (): Promise<AnalysisModel[]> => {
      const res = await apiClient<AnalysisModelsResponse>('/analysis-models?active=true');
      return res.data;
    },
  });
}

/** GET /analysis-models (unfiltered) — the catalog admin page's own list, not the run picker's active-only list. */
export function useAllAnalysisModels() {
  return useQuery({
    queryKey: ['analysis-models', 'all'],
    queryFn: async (): Promise<AnalysisModel[]> => {
      const res = await apiClient<AnalysisModelsResponse>('/analysis-models');
      return res.data;
    },
  });
}

function useInvalidateAnalysisModels() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['analysis-models'] });
}

export type AnalysisModelDraft = Partial<AnalysisModel>;

export function useCreateAnalysisModel() {
  const invalidate = useInvalidateAnalysisModels();
  return useMutation({
    mutationFn: (draft: AnalysisModelDraft) =>
      apiClient<{ success: boolean; data: AnalysisModel }>('/analysis-models', {
        method: 'POST',
        body: JSON.stringify(draft),
      }),
    onSuccess: invalidate,
  });
}

export function useUpdateAnalysisModel() {
  const invalidate = useInvalidateAnalysisModels();
  return useMutation({
    mutationFn: ({ id, ...fields }: AnalysisModelDraft & { id: number }) =>
      apiClient<{ success: boolean; data: AnalysisModel }>(`/analysis-models/${id}`, {
        method: 'PUT',
        body: JSON.stringify(fields),
      }),
    onSuccess: invalidate,
  });
}

export function useDeleteAnalysisModel() {
  const invalidate = useInvalidateAnalysisModels();
  return useMutation({
    mutationFn: (id: number) => apiClient<{ success: boolean }>(`/analysis-models/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}
